import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  useCallback, useRef,
} from "react";
import {
  ICheckout,
  ICheckoutTotals,
  ICartBundleItem,
  ILineItem,
  IShippingAddress,
  IShippingRate,
} from "./types/types";
import createCheckoutFromCart from "./utils/createCheckoutFromCart";
import {useParams, useSearchParams} from "react-router-dom";
import config from "./utils/config";
import { storefrontSdk } from "./utils/sdk";
import {
  CartBuyerIdentityInput,
  GetCartQuery,
  CountryCode,
  DeliveryAddressValidationStrategy,
} from "./types/storefront.generated";
import { postcodeToRegion } from "./utils/postcodeToRegion";

import HyperDX from '@hyperdx/browser';
import {findAddressInCart} from "./utils/findAddressInCart";
import {debounce} from "./utils/debounce";
import { isBundleAvailableForRegion } from "./utils/bundlePricing";
import { parseJsonResponse, retryTransientJson } from "./utils/retryTransientJson";
import { assertBuyerIdentityUpdated } from "./utils/buyerIdentityUpdate";

HyperDX.init({
  apiKey: process.env.REACT_APP_HYPERDX_API_KEY ?? '',
  service: `checkout-frontend-${config.countryCode}-${config.environment}`,
  tracePropagationTargets: [/checkout.bushbuck.dev/i],
  consoleCapture: true,
  maskAllInputs: false,
});

export type CartAddressUserErrorDetail = {
  field?: Array<string | null> | null;
  message: string;
};

export class CartAddressUserError extends Error {
  userErrors: CartAddressUserErrorDetail[];

  constructor(userErrors: CartAddressUserErrorDetail[]) {
    super(userErrors.map((error) => error.message).join(" "));
    this.name = "CartAddressUserError";
    this.userErrors = userErrors;
  }
}

interface CartContextValues {
  checkout: ICheckout | null;
  cartLoading: boolean;
  paymentInProgress: boolean;
  setPaymentInProgress: (inProgress: boolean) => void;
  totalsLoading: boolean;
  totalsError: string | null;
  checkoutTotals: ICheckoutTotals | null;
  shippingRates: IShippingRate[] | null;
  addDiscountCode: (discountCode: string) => Promise<void>;
  removeDiscountCode: () => Promise<void>;
  addGiftCard: (GiftCardCode: string) => Promise<void>;
  removeGiftCard: () => Promise<void>;
  updateEmail: (email: string | null) => Promise<void>;
  updateCustomerPreferredAddress: (
    address: IShippingAddress | null,
  ) => Promise<void>;
  updateShippingAddress: (
    email: string | null,
    phone: string | null,
    address: IShippingAddress | null,
  ) => Promise<void>;
  removeUserFromCart: () => Promise<void>;
  updateShippingMethod: (groupId: string, handle: string) => Promise<void>;
  toggleEmailMarketingOptIn: () => void;
  validateDiscountCodes: () => Promise<string | null>;
  updateCharityDonation: (donation: { sku: string; amount: number } | null) => Promise<void>;
}

const CartContext = createContext<CartContextValues | undefined>(undefined);

function throwCartAddressUserErrors(
  userErrors: CartAddressUserErrorDetail[] | undefined,
) {
  if (userErrors?.length) {
    throw new CartAddressUserError(userErrors);
  }
}

interface CartProviderProps {
  children: ReactNode;
}

type PriceOverride = {
  price: string;
  quantity?: number;
  discountNote?: string;
  restorePrice?: boolean;
};

type PriceOverrides = Record<string, PriceOverride>;

function applyPriceOverridesToCheckout(
  checkout: ICheckout | null,
  priceOverrides: PriceOverrides,
) {
  if (!checkout || Object.keys(priceOverrides).length === 0) {
    return checkout;
  }

  let changed = false;
  const remainingOverrideQuantities = new Map(
    Object.entries(priceOverrides).map(([variantId, priceOverride]) => [
      variantId,
      priceOverride.quantity ?? Number.MAX_SAFE_INTEGER,
    ]),
  );
  const lineItems = checkout.lineItems.flatMap((lineItem) => {
    const priceOverride = priceOverrides[lineItem.variantId];
    const priceOverrideValue = priceOverride?.price;
    const remainingOverrideQuantity = remainingOverrideQuantities.get(lineItem.variantId) ?? 0;
    if (
      priceOverrideValue &&
      remainingOverrideQuantity > 0 &&
      lineItem.discountNote &&
      lineItem.discountNote === priceOverride.discountNote
    ) {
      const overriddenQuantity = Math.min(lineItem.quantity, remainingOverrideQuantity);
      remainingOverrideQuantities.set(
        lineItem.variantId,
        remainingOverrideQuantity - overriddenQuantity,
      );
      if (overriddenQuantity >= lineItem.quantity) {
        return [lineItem];
      }

      changed = true;
      return [
        {
          ...lineItem,
          quantity: overriddenQuantity,
        },
        {
          ...lineItem,
          quantity: lineItem.quantity - overriddenQuantity,
          price: getStandardPriceForOverrideRemainder(lineItem),
          comparePrice: null,
          discountNote: null,
        },
      ];
    }

    if (
      !priceOverrideValue ||
      remainingOverrideQuantity <= 0 ||
      (parseFloat(priceOverrideValue) >= parseFloat(lineItem.price) && !priceOverride.restorePrice)
    ) {
      return [lineItem];
    }

    changed = true;
    const overriddenQuantity = Math.min(lineItem.quantity, remainingOverrideQuantity);
    remainingOverrideQuantities.set(
      lineItem.variantId,
      remainingOverrideQuantity - overriddenQuantity,
    );

    const overriddenLineItem = {
      ...lineItem,
      quantity: overriddenQuantity,
      price: priceOverrideValue,
      comparePrice: priceOverride.restorePrice && !priceOverride.discountNote
        ? parseFloat(lineItem.comparePrice ?? "0") > parseFloat(priceOverrideValue)
          ? lineItem.comparePrice
          : null
        : getComparePriceForOverride(lineItem, priceOverrideValue),
      discountNote: priceOverride.discountNote ?? null,
    };

    if (overriddenQuantity >= lineItem.quantity) {
      return [overriddenLineItem];
    }

    return [
      overriddenLineItem,
      {
        ...lineItem,
        quantity: lineItem.quantity - overriddenQuantity,
        discountNote: null,
      },
    ];
  });

  return changed ? { ...checkout, lineItems } : checkout;
}

function getStandardPriceForOverrideRemainder(lineItem: ILineItem) {
  return lineItem.comparePrice ?? lineItem.price;
}

function getComparePriceForOverride(lineItem: ILineItem, priceOverride: string) {
  const comparePrice = lineItem.comparePrice ?? lineItem.price;
  return parseFloat(comparePrice) > parseFloat(priceOverride)
    ? comparePrice
    : null;
}

function hasDeliveryOptions(cart: GetCartQuery["cart"]) {
  return Boolean(
    cart?.deliveryGroups?.nodes.some((group) => group.deliveryOptions.length > 0),
  );
}

function cartContainsPhysicalItems(cart: GetCartQuery["cart"]) {
  if (!cart) {
    return false;
  }

  const hasBundleItems = cart.bundles?.value
    ? (JSON.parse(cart.bundles.value) as ICartBundleItem[]).some((bundle) =>
      isBundleAvailableForRegion(bundle.bundle),
    )
    : false;

  return hasBundleItems || cart.lines.nodes.some(line =>
    "merchandise" in line && line.merchandise?.requiresShipping !== false
  );
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<GetCartQuery["cart"] | null>(null);
  const [checkout, setCheckout] = useState<ICheckout | null>(null);
  const [checkoutTotals, setCheckoutTotals] = useState<ICheckoutTotals | null>(
    null,
  );
  const [shippingRates, setShippingRates] = useState<IShippingRate[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [totalsError, setTotalsError] = useState<string | null>(null);
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [cartPricingVersion, setCartPricingVersion] = useState(0);
  const queryParams = useParams();
  const [searchParams] = useSearchParams();
  const cartKey = searchParams.get('key');
  const cartId = `${queryParams.cartId}?key=${cartKey}`;
  if (cartId) {
    HyperDX.setGlobalAttributes({
      cartId: `gid://shopify/Cart/${cartId}`,
      environment: config.environment,
      countryCode: config.countryCode,
    });
    HyperDX.addAction('Checkout Started', {
      cartId: `gid://shopify/Cart/${cartId}`,
    });
  }
  const storefront = useMemo(() => storefrontSdk(), []);

  const debouncedFetchRef = useRef<any>(null);
  const totalsRequestSeqRef = useRef(0);
  const appliedAffiliateReferralRef = useRef(false);

  // Use for Shopify cart updates that can affect draft-order totals. Display-only
  // checkout updates should call setCheckout without bumping cartPricingVersion.
  const setCartAndRefreshTotals = useCallback((cart: GetCartQuery["cart"]) => {
    setCart(cart);
    setCartPricingVersion((version) => version + 1);
  }, []);

  const fetchOrderTotalsOriginal = useCallback(
    async (cartId: string) => {
      if (error) {
        return;
      }
      const requestSeq = totalsRequestSeqRef.current + 1;
      totalsRequestSeqRef.current = requestSeq;
      try {
        setTotalsLoading(true);
        setTotalsError(null);
        const { totals, priceOverrides } = await retryTransientJson(
          async () => {
            const response = await fetch(
              `${config.checkoutUrl}/api/calculateOrder`,
              {
                method: "POST",
                body: JSON.stringify({
                  cartId: cartId,
                }),
              },
            );
            const responseData = await parseJsonResponse<{
              totals: ICheckoutTotals;
              priceOverrides?: PriceOverrides;
              error?: string;
            }>(response);
            if (!response.ok) {
              throw new Error(responseData.error ?? "There was an error calculating your order");
            }

            return responseData;
          },
          {
            onRetry: (error, attempt) => console.warn(
              `Retrying order totals after transient JSON parse failure, attempt ${attempt}`,
              error,
            ),
          },
        );
        if (requestSeq !== totalsRequestSeqRef.current) {
          return;
        }
        setCheckoutTotals(totals);
        if (priceOverrides) {
          setCheckout((currentCheckout) =>
            applyPriceOverridesToCheckout(currentCheckout, priceOverrides),
          );
        }
      } catch (e) {
        if (requestSeq !== totalsRequestSeqRef.current) {
          return;
        }
        console.error(e);
        setTotalsError("There was an error calculating your order, please try again");
      } finally {
        if (requestSeq === totalsRequestSeqRef.current) {
          setTotalsLoading(false);
        }
      }
    },
    [error],
  );
  useEffect(() => {
    debouncedFetchRef.current = debounce(fetchOrderTotalsOriginal, 750);
  }, [fetchOrderTotalsOriginal]);

// The function we'll expose and use
  const fetchOrderTotals = useCallback(
    (cartId: string) => {
      setTotalsLoading(true);
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current(cartId);
      }
    },
    []
  );

  const processShippingRates = useCallback(async (cart: GetCartQuery['cart']) => {
    if (!cart) return;

    if (!cartContainsPhysicalItems(cart)) {
      setShippingRates([{
        title: 'Digital Delivery',
        price: { amount: '0.00', currencyCode: config.currencyCode },
        handle: 'digital-delivery'
      }]);
      return;
    }
  }, []);

  const addDefaultDeliveryAddress = useCallback(
    async (cart: GetCartQuery["cart"]) => {
      if (
        !cart ||
        !cartContainsPhysicalItems(cart) ||
        hasDeliveryOptions(cart)
      ) {
        return cart;
      }

      const cartDeliveryAddressResponse = await storefront.addDeliveryAddress({
        cartId: cart.id,
        addresses: [
          {
            address: {
              deliveryAddress: {
                firstName: "",
                lastName: "",
                address1: "",
                address2: "",
                city: config.defaultShippingCity,
                provinceCode: config.defaultShippingProvince,
                zip: "",
                countryCode: config.countryCode as CountryCode,
                phone: "",
              },
            },
            selected: true,
            oneTimeUse: true,
            validationStrategy: DeliveryAddressValidationStrategy.CountryCodeOnly,
          },
        ],
        country: config.countryCode as CountryCode,
      });

      throwCartAddressUserErrors(
        cartDeliveryAddressResponse.cartDeliveryAddressesAdd?.userErrors,
      );

      return cartDeliveryAddressResponse.cartDeliveryAddressesAdd?.cart ?? cart;
    },
    [storefront],
  );

  const removeNonLocalDeliveryAddresses = useCallback(
    async (cart: GetCartQuery["cart"]) => {
      if (!cart) {
        return cart;
      }

      const invalidAddressIds = cart.delivery.addresses
        .filter((deliveryAddress) => deliveryAddress.address.countryCode !== config.countryCode)
        .map((deliveryAddress) => deliveryAddress.id);

      if (invalidAddressIds.length === 0) {
        return cart;
      }

      const response = await storefront.removeDeliveryAddresses({
        cartId: cart.id,
        addressIds: invalidAddressIds,
        country: config.countryCode as CountryCode,
      });

      throwCartAddressUserErrors(
        response.cartDeliveryAddressesRemove?.userErrors,
      );

      return response.cartDeliveryAddressesRemove?.cart ?? cart;
    },
    [storefront],
  );

  const updateEmail = useCallback(
    async (
      email: string | null,
      cartIdOverride?: string,
      phoneOverride?: string | null,
    ) => {
      const targetCartId = cartIdOverride ?? checkout?.cartId;
      const targetPhone = phoneOverride ?? checkout?.phone;

      if (!targetCartId) {
        console.error("Invalid cart ID");
        return;
      }
      if (email) {
        HyperDX.setGlobalAttributes({
          userEmail: email
        });
        HyperDX.addAction('Email set', {
          userEmail: email
        });
      }
      try {
        let buyerIdentity: CartBuyerIdentityInput = {};
        if (targetPhone) {
          buyerIdentity.phone = targetPhone;
        }

        buyerIdentity.email = email;
        const cartData = await storefront.updateCartBuyerIdentity({
          buyerIdentity,
          cartId: targetCartId,
          country: config.countryCode as CountryCode,
        });
        if (cartData.cartBuyerIdentityUpdate?.cart) {
          setCheckout((currentCheckout) => currentCheckout
            ? {
              ...currentCheckout,
              email: cartData.cartBuyerIdentityUpdate?.cart?.buyerIdentity.email ?? email,
              phone: cartData.cartBuyerIdentityUpdate?.cart?.buyerIdentity.phone ?? targetPhone ?? null,
            }
            : currentCheckout,
          );
          setCart(cartData.cartBuyerIdentityUpdate.cart);
          processShippingRates(cartData.cartBuyerIdentityUpdate.cart);
        }
      } catch (e) {
        throw e;
      } finally {
        setCartLoading(false);
      }
    },
    [
      processShippingRates,
      checkout?.cartId,
      checkout?.phone,
      storefront,
    ],
  );

  const fetchCart = useCallback(
    async (cartId: string) => {
      try {
        setCartLoading(true);
        await retryTransientJson(async () => {
          const cartData = await storefront.getCart({
            cartId: "gid://shopify/Cart/" + cartId,
            country: config.countryCode as CountryCode,
          });
          const sanitizedCart = await removeNonLocalDeliveryAddresses(cartData.cart);
          let cartWithDefaultAddress = sanitizedCart;
          try {
            cartWithDefaultAddress = await addDefaultDeliveryAddress(sanitizedCart);
          } catch (error) {
            console.warn("Unable to add default delivery address", error);
            const refreshedCartData = await storefront.getCart({
              cartId: "gid://shopify/Cart/" + cartId,
              country: config.countryCode as CountryCode,
            });
            cartWithDefaultAddress = refreshedCartData.cart ?? sanitizedCart;
          }
          const buyerIdentity = cartWithDefaultAddress?.buyerIdentity;
          const checkoutData = await createCheckoutFromCart(cartWithDefaultAddress);
          setCheckout(checkoutData);
          setCartAndRefreshTotals(cartWithDefaultAddress);

          processShippingRates(cartWithDefaultAddress);

          if (!buyerIdentity?.email && buyerIdentity?.customer?.email) {
            await updateEmail(
              buyerIdentity.customer.email,
              checkoutData.cartId,
              checkoutData.phone,
            );
          }
        }, {
          onRetry: (error, attempt) => console.warn(
            `Retrying cart fetch after transient JSON parse failure, attempt ${attempt}`,
            error,
          ),
        });
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        setCartLoading(false);
      }
    },
    [updateEmail, processShippingRates, removeNonLocalDeliveryAddresses, addDefaultDeliveryAddress, setCartAndRefreshTotals, storefront],
  );

  useEffect(() => {
    if (cartId && !cart) {
      fetchCart(cartId).catch((error) => {
        setError(error instanceof Error ? error : new Error(String(error)));
      });
    }
  }, [cart, cartId, fetchCart]);

  useEffect(() => {
    if (!cart?.id) {
      return;
    }
    fetchOrderTotals(cart.id);
  }, [cart?.id, cartPricingVersion, fetchOrderTotals]);

  const updateCharityDonation = useCallback(async (
    donation: { sku: string; amount: number } | null,
  ) => {
    if (!cart) {
      throw new Error("Invalid cart ID");
    }

    setCartLoading(true);
    try {
      if (donation) {
        const result = await storefront.setCartMetafields({
          metafields: [{
            key: "charityDonation",
            ownerId: cart.id,
            type: "json",
            value: JSON.stringify(donation),
          }],
        });
        throwCartAddressUserErrors(result.cartMetafieldsSet?.userErrors);
      } else {
        const result = await storefront.removeCartMetafield({
          input: {
            key: "charityDonation",
            ownerId: cart.id,
          },
        });
        throwCartAddressUserErrors(result.cartMetafieldDelete?.userErrors);
      }

      await fetchOrderTotalsOriginal(cart.id);
    } catch (error) {
      setTotalsError(error instanceof Error ? error.message : "Unable to save charity donation");
      throw error;
    } finally {
      setCartLoading(false);
    }
  }, [cart, fetchOrderTotalsOriginal, storefront]);

  const addDiscountCode = useCallback(async (discountCode: string) => {
    if (!checkout?.cartId) {
      throw new Error("Invalid cart ID");
    }
    setCartLoading(true);
    try {
      const response = await fetch(`${config.checkoutUrl}/api/applyDiscountCode`, {
        method: "POST",
        body: JSON.stringify({
          cartId: checkout.cartId,
          discountCode,
        }),
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.error ?? "Unable to apply discount code");
      }

      if (responseData.cart) {
        const checkoutData = await createCheckoutFromCart(responseData.cart);
        setCheckout(checkoutData);
        setCartAndRefreshTotals(responseData.cart);
        processShippingRates(responseData.cart);
      }
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setCartLoading(false);
    }
  }, [checkout?.cartId, processShippingRates, setCartAndRefreshTotals]);

  const removeDiscountCode = async () => {
    await addDiscountCode("");
  };

  useEffect(() => {
    const affiliateCode = searchParams.get("ref")?.trim();

    if (!affiliateCode || !checkout?.cartId || appliedAffiliateReferralRef.current) {
      return;
    }

    appliedAffiliateReferralRef.current = true;
    addDiscountCode(affiliateCode).catch((error) => {
      console.warn("Unable to apply affiliate referral code", error);
    });
  }, [checkout?.cartId, searchParams, addDiscountCode]);

  const validateDiscountCodes = async () => {
    if (!checkout?.cartId) {
      throw new Error("Invalid cart ID");
    }
    setCartLoading(true);
    try {
      const cartData = await storefront.getCart({
        cartId: checkout.cartId,
        country: config.countryCode as CountryCode,
      });

      if (!cartData?.cart?.discountCodes || cartData.cart?.discountCodes.length === 0) {
        return null;
      }

      const unapplicableDiscountCode = cartData?.cart?.discountCodes.find((code) => {
        const isPartnersCode = config.partnerCodes.includes(code.code.toUpperCase());
        return code.code !== "" && !isPartnersCode && code.applicable === false;
      });

      if (unapplicableDiscountCode) {
        throw new Error(`Discount code "${unapplicableDiscountCode.code}" has already been used.`);
      }

      return null;
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setCartLoading(false);
    }
  };

  const addGiftCard = async (giftCardCode: string) => {
    if (!config.giftCardsEnabled) {
      throw new Error("Gift cards are not available for this checkout.");
    }
    if (!checkout?.cartId) {
      throw new Error("Invalid cart ID");
    }
    setCartLoading(true);
    try {
      await storefront.updateGiftCard({
        cartId: checkout.cartId,
        giftCardCodes: [giftCardCode],
        country: config.countryCode as CountryCode,
      });

      // Fetch the updated cart
      await fetchCart(cartId);
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setCartLoading(false);
    }
  };

  const removeGiftCard = async () => {
    if (!checkout?.cartId) {
      throw new Error("Invalid cart ID");
    }
    setCartLoading(true);
    try {
      // Find any applied gift cards to remove
      const appliedGiftCardIds = cart?.appliedGiftCards?.map(card => card.id) || [];

      if (appliedGiftCardIds.length === 0) {
        console.warn("No gift cards to remove");
        return;
      }

      const response = await storefront.removeGiftcard({
        appliedGiftCardIds,
        cartId: checkout.cartId,
        country: config.countryCode as CountryCode,
      });

      if (response.cartGiftCardCodesRemove?.userErrors?.length) {
        const errorMessage = response.cartGiftCardCodesRemove.userErrors[0].message;
        console.error("Error removing gift card:", errorMessage);
        throw new Error(errorMessage);
      }

      // Fetch the updated cart
      await fetchCart(cartId);

    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setCartLoading(false);
    }
  };

  const updateShippingAddress = useCallback(
    async (
      email: string | null,
      phone: string | null,
      shippingAddress: IShippingAddress | null = null,
    ) => {
      if (!checkout) {
        return;
      }
      setCartLoading(true);

      try {
        // if (email || phone) {
        //   let buyerIdentity: CartBuyerIdentityInput = {};
        //   if (email) {
        //     buyerIdentity.email = email;
        //   }
        //   if (phone) {
        //     buyerIdentity.phone = phone;
        //   }
        //
        //   await storefront.updateCartBuyerIdentity({
        //     buyerIdentity,
        //     cartId: checkout.cartId,
        //   });
        // }

        // Then update delivery address using the appropriate API
        if (shippingAddress) {
          if (shippingAddress?.zip) {
            const region = postcodeToRegion(shippingAddress.zip, config.countryCode);
            shippingAddress.provinceCode = region.code;
          }

          const deliveryAddress = {
            firstName: shippingAddress?.firstName || '',
            lastName: shippingAddress?.lastName || '',
            address1: shippingAddress?.address1 || '',
            address2: shippingAddress?.address2 || '',
            city: shippingAddress?.city || '',
            provinceCode: shippingAddress?.provinceCode || '',
            zip: shippingAddress?.zip || '',
            countryCode: config.countryCode as CountryCode,
            phone: shippingAddress?.phone || ''
          };

          if (cart?.delivery.addresses && cart.delivery.addresses.length > 0) {
            const existingAddress = findAddressInCart(deliveryAddress, cart.delivery.addresses);
            if (existingAddress) {
              const cartDeliveryAddressUpdateResponse = await storefront.updateDeliveryAddress({
                cartId: checkout.cartId,
                addresses: [
                  {
                    id: existingAddress.id,
                    address: { deliveryAddress },
                    selected: true,
                    oneTimeUse: true,
                  }
                ],
                country: config.countryCode as CountryCode,
              });
              throwCartAddressUserErrors(
                cartDeliveryAddressUpdateResponse.cartDeliveryAddressesUpdate
                  ?.userErrors,
              );
              if (cartDeliveryAddressUpdateResponse.cartDeliveryAddressesUpdate?.cart) {
                const checkoutData = await createCheckoutFromCart(
                  cartDeliveryAddressUpdateResponse.cartDeliveryAddressesUpdate.cart
                );
                setCheckout(checkoutData);
                setCartAndRefreshTotals(cartDeliveryAddressUpdateResponse.cartDeliveryAddressesUpdate.cart);
                await processShippingRates(cartDeliveryAddressUpdateResponse.cartDeliveryAddressesUpdate.cart);
                return;
              }
            }
          }

          const cartDeliveryAddressResponse = await storefront.addDeliveryAddress({
            cartId: checkout.cartId,
            addresses: [
              {
                address: { deliveryAddress },
                selected: true,
                oneTimeUse: true,
              }
            ],
            country: config.countryCode as CountryCode,
          });

          throwCartAddressUserErrors(
            cartDeliveryAddressResponse.cartDeliveryAddressesAdd?.userErrors,
          );
          if (cartDeliveryAddressResponse.cartDeliveryAddressesAdd?.cart) {
            const checkoutData = await createCheckoutFromCart(
              cartDeliveryAddressResponse.cartDeliveryAddressesAdd.cart
            );
            setCheckout(checkoutData);
            setCartAndRefreshTotals(cartDeliveryAddressResponse.cartDeliveryAddressesAdd.cart);
            await processShippingRates(cartDeliveryAddressResponse.cartDeliveryAddressesAdd.cart);
          }
        }
      } catch (e: any) {
        // Check if this is a CONFLICT error (concurrent cart updates)
        const isConflictError = e?.response?.errors?.some(
          (error: any) => error?.extensions?.code === 'CONFLICT'
        );

        if (isConflictError) {
          console.warn("Cart update conflict (concurrent requests) - this is expected and non-blocking");
          return;
        }

        // For other errors, log and re-throw
        console.error("Error updating shipping address:", e);
        throw e;
      } finally {
        setCartLoading(false);
      }
    },
    [checkout, cart?.delivery.addresses, storefront, processShippingRates, setCartAndRefreshTotals],
  );

  const updateCustomerPreferredAddress = useCallback(
    async (customerAddress: IShippingAddress | null = null) => {
      if (!checkout?.cartId || !customerAddress || !customerAddress.id) {
        return;
      }
      if (customerAddress.country !== config.countryCode) {
        return;
      }
      setCartLoading(true);

      try {
        const cartDeliveryAddressResponse = await storefront.addDeliveryAddress({
          cartId: checkout.cartId,
          addresses: [
            {
              address: {
                copyFromCustomerAddressId: customerAddress.id
              },
              oneTimeUse: true,
              selected: true,
            }
          ],
          country: config.countryCode as CountryCode,
        });

        throwCartAddressUserErrors(
          cartDeliveryAddressResponse.cartDeliveryAddressesAdd?.userErrors,
        );
        if (cartDeliveryAddressResponse.cartDeliveryAddressesAdd?.cart) {
          const checkoutData = await createCheckoutFromCart(
            cartDeliveryAddressResponse.cartDeliveryAddressesAdd.cart
          );
          setCheckout(checkoutData);
          setCartAndRefreshTotals(cartDeliveryAddressResponse.cartDeliveryAddressesAdd.cart);
          processShippingRates(cartDeliveryAddressResponse.cartDeliveryAddressesAdd.cart);
        }
      } catch (e: any) {
        // Check if this is a CONFLICT error (concurrent cart updates)
        const isConflictError = e?.response?.errors?.some(
          (error: any) => error?.extensions?.code === 'CONFLICT'
        );

        if (isConflictError) {
          // This is a non-blocking conflict - one of the concurrent requests will succeed
          // Just log it as a warning and don't propagate the error
          console.warn("Cart update conflict (concurrent requests) - this is expected and non-blocking");
          return;
        }

        // For other errors, re-throw
        throw e;
      } finally {
        setCartLoading(false);
      }
    },
    [checkout?.cartId, storefront, processShippingRates, setCartAndRefreshTotals],
  );

  const updateShippingMethod = useCallback(
    async (groupId: string, handle: string) => {
      if (!checkout?.cartId) {
        throw new Error("Invalid cart ID");
      }
      setCartLoading(true);
      try {
        const cartData = await storefront.updateDeliveryOption({
          cartId: checkout.cartId,
          selectedDeliveryOptions: [
            {
              deliveryGroupId: groupId,
              deliveryOptionHandle: handle
            }
          ],
          country: config.countryCode as CountryCode,
        });

        if (cartData.cartSelectedDeliveryOptionsUpdate?.cart) {
          const checkoutData = await createCheckoutFromCart(
            cartData.cartSelectedDeliveryOptionsUpdate.cart
          );
          setCheckout(checkoutData);
          setCartAndRefreshTotals(cartData.cartSelectedDeliveryOptionsUpdate.cart);
          processShippingRates(cartData.cartSelectedDeliveryOptionsUpdate.cart);
        }
      } catch (e) {
        throw e;
      } finally {
        setCartLoading(false);
      }
    },
    [checkout?.cartId, storefront, processShippingRates, setCartAndRefreshTotals],
  );

  const removeUserFromCart = useCallback(async () => {
    if (!checkout) {
      return;
    }
    setCartLoading(true);
    let buyerIdentity: CartBuyerIdentityInput = {
      customerAccessToken: null,
    };

    try {
      const cartData = await storefront.updateCartBuyerIdentity({
        buyerIdentity,
        cartId: checkout.cartId,
        country: config.countryCode as CountryCode,
      });
      // A rejected detach (userErrors, or no cart back) used to fall through
      // silently: the form then cleared the customer while the cart kept its
      // token and club pricing. Throw instead so the caller can tell the
      // shopper and leave the logged-in state intact.
      const cart = assertBuyerIdentityUpdated(cartData.cartBuyerIdentityUpdate, "logout");
      const checkoutData = await createCheckoutFromCart(cart);
      setCheckout(checkoutData);
      setCartAndRefreshTotals(cart);
      processShippingRates(cart);
    } finally {
      setCartLoading(false);
    }
  }, [checkout, storefront, processShippingRates, setCartAndRefreshTotals]);

  const toggleEmailMarketingOptIn = () => {
    if (!checkout) {
      return;
    }
    setCheckout({
      ...checkout,
      emailMarketingOptIn: !checkout.emailMarketingOptIn,
    });
  };

  useEffect(() => {
    if (!checkout?.shippingAddress && checkout?.customer?.preferredAddress) {
      updateCustomerPreferredAddress(checkout.customer.preferredAddress);
    }
  }, [checkout?.shippingAddress, checkout?.customer?.preferredAddress, updateCustomerPreferredAddress]);

  if (error) {
    // Display the error component if an error occurred
    return (
      <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8 mt-10">
        <div className="text-center">
          <p className="text-base font-semibold text-gray-600">Whoops!</p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Something went wrong
          </h1>
          <p className="mt-6 text-base leading-7 text-gray-600">
            Sorry, we were unable to retrieve the checkout details. The cart may
            be expired
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <a
              href={config.storeUrl}
              className="rounded-md bg-gray-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
            >
              Back to store
            </a>
            <a
              href={`${config.storeUrl}/pages/contact-us`}
              className="text-sm font-semibold text-gray-900"
            >
              Contact support <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
        </div>
      </main>
    );
  }

  const contextValues: CartContextValues = {
    checkout,
    checkoutTotals,
    shippingRates,
    paymentInProgress,
    setPaymentInProgress,
    addDiscountCode,
    removeDiscountCode,
    addGiftCard,
    removeGiftCard,
    updateCustomerPreferredAddress,
    updateShippingAddress,
    updateEmail,
    updateShippingMethod,
    removeUserFromCart,
    cartLoading,
    totalsLoading,
    totalsError,
    toggleEmailMarketingOptIn,
    validateDiscountCodes,
    updateCharityDonation,
  };

  return (
    <CartContext.Provider value={contextValues}>
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCartContext must be used within a CartProvider");
  }

  return context;
};
